import axios from "axios";

const BASE = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const http = axios.create({ baseURL: BASE });

// Token storage: "Remember me" -> localStorage (persists); otherwise sessionStorage
// (cleared when the browser/tab closes). Reads check both.
// The KEY is display-mode aware: an installed PWA (standalone) and the browser
// share storage on Android, so we namespace the token per context. This lets the
// same phone hold TWO independent logins — e.g. a worker in the installed app and
// a supervisor in the browser — without one clobbering the other.
const IS_APP = typeof window !== "undefined" && (
  window.matchMedia("(display-mode: standalone)").matches ||
  window.matchMedia("(display-mode: fullscreen)").matches ||
  window.matchMedia("(display-mode: minimal-ui)").matches ||
  window.navigator.standalone === true
);
const TOKEN_KEY = IS_APP ? "pmx_token_app" : "pmx_token_web";
export const tokenStore = {
  get: () => localStorage.getItem(TOKEN_KEY) || sessionStorage.getItem(TOKEN_KEY),
  set: (t, remember = true) => {
    if (remember) {
      localStorage.setItem(TOKEN_KEY, t); sessionStorage.removeItem(TOKEN_KEY);
      // Ask the browser not to clear this site's storage when the phone runs low on space.
      navigator.storage?.persist?.().catch(() => {});
    } else { sessionStorage.setItem(TOKEN_KEY, t); localStorage.removeItem(TOKEN_KEY); }
  },
  // Swap in a renewed token wherever the current one lives.
  replace: (t) => (localStorage.getItem(TOKEN_KEY) ? localStorage : sessionStorage).setItem(TOKEN_KEY, t),
  clear: () => { localStorage.removeItem(TOKEN_KEY); sessionStorage.removeItem(TOKEN_KEY); },
  remembered: () => !!localStorage.getItem(TOKEN_KEY),
};

// While the platform owner works inside a company, their own login waits here (this tab only).
const OWNER_RETURN_KEY = "cj_owner_return";
export const ownerReturn = {
  get: () => { try { return JSON.parse(sessionStorage.getItem(OWNER_RETURN_KEY) || "null"); } catch { return null; } },
  set: (v) => sessionStorage.setItem(OWNER_RETURN_KEY, JSON.stringify(v)),
  clear: () => sessionStorage.removeItem(OWNER_RETURN_KEY),
};

// The depot a company login is viewing ("" = All depots). Sent on every request.
const DEPOT_KEY = "cj_depot";
export const depotStore = {
  get: () => localStorage.getItem(DEPOT_KEY) || "",
  set: (id) => (id ? localStorage.setItem(DEPOT_KEY, id) : localStorage.removeItem(DEPOT_KEY)),
};

http.interceptors.request.use((config) => {
  const token = tokenStore.get();
  if (token) config.headers.Authorization = `Bearer ${token}`;
  const depot = depotStore.get();
  if (depot) config.headers["X-Depot-Id"] = depot;
  return config;
});

http.interceptors.response.use(
  (res) => res,
  (err) => {
    const st = err?.response?.status;
    if (st === 402) window.dispatchEvent(new CustomEvent("cj-open-billing", { detail: { reason: err.response.data?.detail || "" } }));
    if (st === 423) window.dispatchEvent(new Event("cj-billing-locked"));
    // Only sign out when the token that was rejected is still the one we hold (not an older one).
    const sent = String(err?.config?.headers?.Authorization || "");
    if (st === 401 && tokenStore.get() && sent === `Bearer ${tokenStore.get()}`) {
      tokenStore.clear();
      if (window.location.pathname !== "/login") window.location.assign("/login");
    }
    return Promise.reject(err);
  }
);

export const api = {
  register: (payload) => http.post("/auth/register", payload).then((r) => r.data),
  registerCompany: (payload) => http.post("/auth/register-company", payload).then((r) => r.data),
  registerCompanyGoogle: (payload) => http.post("/auth/register-company-google", payload).then((r) => r.data),
  sendEnquiry: (payload) => http.post("/enquiries", payload).then((r) => r.data),
  login: (payload) => http.post("/auth/login", payload).then((r) => r.data),
  session: (session_id) => http.post("/auth/session", { session_id }).then((r) => r.data),
  forgotPassword: (email) => http.post("/auth/forgot-password", { email }).then((r) => r.data),
  resetPassword: (token, new_password) => http.post("/auth/reset-password", { token, new_password }).then((r) => r.data),
  me: () => http.get("/auth/me", { timeout: 20000 }).then((r) => r.data),
  renew: () => http.post("/auth/renew").then((r) => r.data),
  logout: () => http.post("/auth/logout").then((r) => r.data),
  restoreData: (opts = {}) => http.post("/admin/restore-from-upstream", opts).then((r) => r.data),
  updatePreferences: (payload) => http.patch("/preferences", payload).then((r) => r.data),
  company: () => http.get("/company").then((r) => r.data),
  billing: () => http.get("/billing").then((r) => r.data),
  subscribe: (payload) => http.post("/billing/subscribe", payload).then((r) => r.data),
  changePlan: (payload) => http.post("/billing/change", payload).then((r) => r.data),
  cancelPlan: () => http.post("/billing/cancel").then((r) => r.data),
  resumePlan: () => http.post("/billing/resume").then((r) => r.data),
  updateCard: (card_token) => http.post("/billing/card", { card_token }).then((r) => r.data),
  updateCompany: (payload) => http.put("/company", payload).then((r) => r.data),
  setJobTypes: (types) => http.put("/company/job-types", { types }).then((r) => r.data),
  resetDemo: () => http.post("/demo/reset").then((r) => r.data),
  uploadCompanyLogo: (file) => {
    const fd = new FormData();
    fd.append("file", file);
    return http.post("/company/logo", fd).then((r) => r.data);
  },
  removeCompanyLogo: () => http.delete("/company/logo").then((r) => r.data),
  depots: () => http.get("/depots").then((r) => r.data),
  createDepot: (name) => http.post("/depots", { name }).then((r) => r.data),
  renameDepot: (id, name) => http.patch(`/depots/${id}`, { name }).then((r) => r.data),
  depotDetails: (id, body) => http.put(`/depots/${id}/details`, body).then((r) => r.data),
  hoursReport: (params) => http.get("/coordinator/hours-report", { params }).then((r) => r.data),
  deleteDepot: (id) => http.delete(`/depots/${id}`).then((r) => r.data),
  logins: () => http.get("/company/logins").then((r) => r.data),
  createLogin: (payload) => http.post("/company/logins", payload).then((r) => r.data),
  updateLogin: (id, payload) => http.patch(`/company/logins/${id}`, payload).then((r) => r.data),
  deleteLogin: (id) => http.delete(`/company/logins/${id}`).then((r) => r.data),
  previewClientImport: (file) => {
    const fd = new FormData();
    fd.append("file", file);
    return http.post("/clients/import/preview", fd).then((r) => r.data);
  },
  importClientRows: (payload) => http.post("/clients/import/rows", payload).then((r) => r.data),
  joinLink: (depot_id) => http.get("/crew/join-link", { params: { depot_id: depot_id || "" } }).then((r) => r.data),
  resetJoinLink: (depot_id) => http.post("/crew/join-link/reset", null, { params: { depot_id: depot_id || "" } }).then((r) => r.data),
  emailJoinLink: (payload) => http.post("/crew/join-link/email", payload).then((r) => r.data),
  joinInfo: (code) => http.get(`/join/${code}`).then((r) => r.data),
  joinCrew: (code, payload) => http.post(`/join/${code}`, payload).then((r) => r.data),
  acceptJoin: (code) => http.post(`/join/${code}/accept`).then((r) => r.data),
  changeRequests: () => http.get("/change-requests").then((r) => r.data),
  sendChangeRequest: (message, screenshot) => {
    const fd = new FormData();
    fd.append("message", message);
    if (screenshot) fd.append("screenshot", screenshot);
    return http.post("/change-requests", fd).then((r) => r.data);
  },
  ownerCompanies: () => http.get("/owner/companies").then((r) => r.data),
  ownerVisits: (range) => http.get("/owner/visits", { params: { range } }).then((r) => r.data),
  invoiceSettings: () => http.get("/invoice-settings").then((r) => r.data),
  saveInvoiceSettings: (body) => http.put("/invoice-settings", body).then((r) => r.data),
  resetInvoiceSettings: () => http.delete("/invoice-settings").then((r) => r.data),
  invoices: (params) => http.get("/invoices", { params }).then((r) => r.data),
  owingByClient: () => http.get("/invoices/owing-by-client").then((r) => r.data),
  chasePreview: () => http.get("/invoices/chase-overdue").then((r) => r.data),
  chaseSend: () => http.post("/invoices/chase-overdue").then((r) => r.data),
  statementPreview: (client, depotId) => http.get("/statements", { params: { client, depot_id: depotId || undefined } }).then((r) => r.data),
  statementSend: (body) => http.post("/statements/send", body).then((r) => r.data),
  invoice: (id) => http.get(`/invoices/${id}`).then((r) => r.data),
  createInvoice: (jobId) => http.post("/invoices", { job_id: jobId }).then((r) => r.data),
  updateInvoice: (id, body) => http.patch(`/invoices/${id}`, body).then((r) => r.data),
  deleteInvoice: (id) => http.delete(`/invoices/${id}`),
  sendInvoice: (id, to) => http.post(`/invoices/${id}/send`, { to }).then((r) => r.data),
  remindInvoice: (id) => http.post(`/invoices/${id}/remind`).then((r) => r.data),
  markInvoicePaid: (id, body) => http.post(`/invoices/${id}/paid`, body).then((r) => r.data),
  markInvoiceUnpaid: (id) => http.post(`/invoices/${id}/unpaid`).then((r) => r.data),
  voidInvoice: (id) => http.post(`/invoices/${id}/void`).then((r) => r.data),
  stripeConnect: () => http.get("/stripe-connect").then((r) => r.data),
  stripeConnectStart: () => http.post("/stripe-connect/start").then((r) => r.data),
  stripeDisconnect: () => http.post("/stripe-connect/disconnect").then((r) => r.data),
  payView: (token) => http.get(`/pay/${token}`).then((r) => r.data),
  payCheckout: (token) => http.post(`/pay/${token}/checkout`).then((r) => r.data),
  payStatus: (token, sessionId) => http.get(`/pay/${token}/status`, { params: { session_id: sessionId } }).then((r) => r.data),
  ownerFeatures: () => http.get("/owner/features").then((r) => r.data),
  ownerSetFeature: (cid, key, enabled) => http.put(`/owner/companies/${cid}/features`, { key, enabled }).then((r) => r.data),
  ownerRequests: (status) => http.get("/owner/change-requests", { params: status ? { status } : {} }).then((r) => r.data),
  ownerMarkRequest: (id, done) => http.patch(`/owner/change-requests/${id}`, { done }).then((r) => r.data),
  ownerEnter: (cid) => http.post(`/owner/companies/${cid}/enter`).then((r) => r.data),
  ownerPause: (cid, paused) => http.post(`/owner/companies/${cid}/pause`, { paused }).then((r) => r.data),
  ownerSetModules: (cid, modules) => http.put(`/owner/companies/${cid}/modules`, { modules }).then((r) => r.data),
  ownerDeleteCompany: (cid, confirm_name) => http.post(`/owner/companies/${cid}/delete`, { confirm_name }).then((r) => r.data),
  ownerUsers: (q) => http.get("/owner/users", { params: { q } }).then((r) => r.data),
  ownerSetPassword: (uid, password) => http.post(`/owner/users/${uid}/password`, { password }).then((r) => r.data),
  ownerAudit: () => http.get("/owner/audit").then((r) => r.data),
  ownerScreenshot: (id) => http.get(`/owner/change-requests/${id}/screenshot`, { responseType: "blob" }).then((r) => r.data),

  jobs: () => http.get("/jobs").then((r) => r.data),
  completedJobs: () => http.get("/jobs/completed").then((r) => r.data),
  job: (id) => http.get(`/jobs/${id}`).then((r) => r.data),
  createJob: (payload) => http.post("/jobs", payload).then((r) => r.data),
  geocodeSearch: (q) => http.get("/geocode/search", { params: { q } }).then((r) => r.data),
  // Scheduled ("create now or set a date") jobs — local persistence.
  scheduledJobs: () => http.get("/scheduled-jobs").then((r) => r.data),
  createScheduledJob: (payload, show_from, label, team = []) => http.post("/scheduled-jobs", { payload, show_from, label, team }).then((r) => r.data),
  deleteScheduledJob: (id) => http.delete(`/scheduled-jobs/${id}`).then((r) => r.data),
  publishDueJobs: () => http.post("/scheduled-jobs/publish-due").then((r) => r.data),
  // Per-client email-report preference — local persistence.
  clientMeta: () => http.get("/client-meta").then((r) => r.data),
  setClientMeta: (clientId, email_report) => http.put(`/client-meta/${clientId}`, { email_report }).then((r) => r.data),
  updateJob: (id, payload) => http.patch(`/jobs/${id}`, payload).then((r) => r.data),
  deleteJob: (id) => http.delete(`/jobs/${id}`).then((r) => r.data),
  jobEntries: (id) => http.get(`/jobs/${id}/entries`).then((r) => r.data),
  jobPhotos: (id) => http.get(`/jobs/${id}/photos`).then((r) => r.data),
  jobReportLink: (id) => http.get(`/coordinator/jobs/${id}/report-link`).then((r) => r.data),
  jobTeam: (id) => http.get(`/jobs/${id}/team`).then((r) => r.data),
  setJobTeam: (id, members) => http.put(`/jobs/${id}/team`, { members }).then((r) => r.data),
  gpsLog: (id, data) => http.post(`/jobs/${id}/gps-log`, data).then((r) => r.data),
  jobGpsLog: (id) => http.get(`/jobs/${id}/gps-log`).then((r) => r.data),
  gpsFlags: () => http.get("/gps-flags").then((r) => r.data),
  emailJobReport: (jobId) => http.post(`/reports/job/${jobId}/email`).then((r) => r.data),
  emailPayroll: (params) => http.post("/coordinator/payroll-summary/email", params).then((r) => r.data),
  reportsSent: (jobId = "") => http.get("/reports/sent", { params: jobId ? { job_id: jobId } : {} }).then((r) => r.data),
  onJobComplete: (jobId) => http.post(`/reports/job/${jobId}/on-complete`).then((r) => r.data),
  reportSettings: () => http.get("/report-settings").then((r) => r.data),
  setReportSettings: (payload) => http.put("/report-settings", payload).then((r) => r.data),
  vapidKey: () => http.get("/push/vapid-public-key").then((r) => r.data.key),
  pushSubscribe: (subscription) => http.post("/push/subscribe", { subscription }).then((r) => r.data),
  pushTest: () => http.post("/push/test").then((r) => r.data),
  notifyAssignment: (worker_email, job_name, job_id) => http.post("/push/notify-assignment", { worker_email, job_name, job_id }).then((r) => r.data),
  notifyJobUpdate: (worker_email, job_name, job_id) => http.post("/push/notify-update", { worker_email, job_name, job_id }).then((r) => r.data),
  notifyJobRemoved: (worker_email, job_name) => http.post("/push/notify-removed", { worker_email, job_name }).then((r) => r.data),
  notifyJobReschedule: (worker_email, job_name, date, job_id) => http.post("/push/notify-reschedule", { worker_email, job_name, date, job_id }).then((r) => r.data),
  myWeekJobs: (start, end) => http.get("/my/week-jobs", { params: { start, end } }).then((r) => r.data),
  approvals: (weekStart) => http.get("/timesheets/approvals", { params: { week_start: weekStart } }).then((r) => r.data),
  approveWeek: (userId, weekStart, approved) => http.post("/timesheets/approve", { user_id: userId, week_start: weekStart, approved }).then((r) => r.data),
  payrollExports: () => http.get("/payroll-exports").then((r) => r.data),
  logPayrollExport: (data) => http.post("/payroll-exports", data).then((r) => r.data),
  addPhoto: (id, payload) => http.post(`/jobs/${id}/photos`, payload).then((r) => r.data),
  deletePhoto: (photoId) => http.delete(`/photos/${photoId}`).then((r) => r.data),
  setPhotoNote: (photoId, note) => http.put(`/photos/${photoId}/note`, { note }).then((r) => r.data),
  checkIn: (id) => http.post(`/jobs/${id}/check-in`).then((r) => r.data),
  checkOut: (id) => http.post(`/jobs/${id}/check-out`).then((r) => r.data),

  // The upstream /coordinator/stats `workers` count has been observed out of sync
  // with the coordinator's actual crew (e.g. reporting a stale/global figure).
  // /coordinator/workers is this coordinator's real crew list, so we trust its
  // length over the upstream stats field, same principle as the workers() override below.
  stats: async () => {
    const [s, ws] = await Promise.all([
      http.get("/coordinator/stats").then((r) => r.data),
      http.get("/coordinator/workers").then((r) => r.data).catch(() => null),
    ]);
    if (Array.isArray(ws)) s.workers = ws.length;
    return s;
  },
  // The upstream /coordinator/workers returns a per-coordinator display-name
  // snapshot that does NOT update when a worker later edits their profile name.
  // Timesheets resolve names live, so we override each worker's name with the
  // live one (matched by email) whenever it's available.
  workers: async () => {
    const [ws, ts] = await Promise.all([
      http.get("/coordinator/workers").then((r) => r.data),
      http.get("/coordinator/timesheets").then((r) => r.data).catch(() => []),
    ]);
    const liveByEmail = {};
    (ts || []).forEach((s) => {
      const e = (s.user_email || "").toLowerCase();
      if (e && s.user_name && s.user_name !== "Unknown") liveByEmail[e] = s.user_name;
    });
    return (ws || []).map((w) => ({ ...w, name: liveByEmail[(w.email || "").toLowerCase()] || w.name }));
  },
  addWorker: (payload) => http.post("/coordinator/workers", payload).then((r) => r.data),
  updateWorker: (payload) => http.patch("/coordinator/workers", payload).then((r) => r.data),
  removeWorker: (email) => http.delete("/coordinator/workers", { params: { email } }).then((r) => r.data),
  coordinatorTimesheets: () => http.get("/coordinator/timesheets").then((r) => r.data),
  addTimesheet: (payload) => http.post("/coordinator/timesheets", payload).then((r) => r.data),
  editTimesheet: (id, payload) => http.patch(`/coordinator/timesheets/${id}`, payload).then((r) => r.data),

  clients: () => http.get("/clients").then((r) => r.data),  createClient: (payload) => http.post("/clients", payload).then((r) => r.data),
  updateClient: (id, payload) => http.patch(`/clients/${id}`, payload).then((r) => r.data),
  deleteClient: (id) => http.delete(`/clients/${id}`).then((r) => r.data),

  schedule: (weekStart) => http.get("/schedule", { params: { week_start: weekStart } }).then((r) => r.data),
  createSchedule: (payload) => http.post("/schedule", payload).then((r) => r.data),
  deleteSchedule: (id) => http.delete(`/schedule/${id}`).then((r) => r.data),
  notifyRoster: (week_start) => http.post("/schedule/notify-week", { week_start }).then((r) => r.data),
  copyWeek: (from_week_start, to_week_start) => http.post("/schedule/copy-week", { from_week_start, to_week_start }).then((r) => r.data),
  timeOff: () => http.get("/time-off").then((r) => r.data),
  updateTimeOff: (id, status) => http.put(`/time-off/${id}`, { status }).then((r) => r.data),
  deleteTimeOff: (id) => http.delete(`/time-off/${id}`).then((r) => r.data),

  currentShift: () => http.get("/timesheet/current").then((r) => r.data),
  clockIn: () => http.post("/timesheet/clock-in").then((r) => r.data),
  breakStart: () => http.post("/timesheet/break/start").then((r) => r.data),
  breakEnd: () => http.post("/timesheet/break/end").then((r) => r.data),
  clockOut: () => http.post("/timesheet/clock-out").then((r) => r.data),
  shifts: () => http.get("/timesheet/shifts").then((r) => r.data),

  mySchedule: (weekStart) => http.get("/my/schedule", { params: { week_start: weekStart } }).then((r) => r.data),
  respondShift: (id, status) => http.post(`/my/schedule/${id}/respond`, { status }).then((r) => r.data),
  myLinked: () => http.get("/my/linked").then((r) => r.data),
  myTimeOff: () => http.get("/my/time-off").then((r) => r.data),
  applyTimeOff: (payload) => http.post("/my/time-off", payload).then((r) => r.data),

  // reports & presets
  // The upstream /reports/summary always returns completion_photos=0 and
  // issue_photos=0 (top-level and per-job) - it expects a photo `kind` of
  // "completion"/"issue" that the mobile app never actually writes. The real
  // kinds recorded are "before"/"after"/"delivery". We recompute both stats
  // ourselves from the real per-job photos so the tab shows real numbers
  // instead of a wall of zeroes: "after" (post-work proof) -> completion,
  // "before" (site condition on arrival) -> issue. Flagged for confirmation
  // with the customer that this mapping matches how they use before/after photos.
  reportsSummary: async () => {
    const s = await http.get("/reports/summary").then((r) => r.data);
    const perJob = s.per_job || [];
    const counts = await Promise.all(
      perJob.map((j) =>
        http.get(`/jobs/${j.job_id}/photos`).then((r) => r.data).catch(() => [])
      )
    );
    let totalCompletion = 0;
    let totalIssue = 0;
    s.per_job = perJob.map((j, i) => {
      const photos = counts[i] || [];
      const completion_photos = photos.filter((p) => p.kind === "after").length;
      const issue_photos = photos.filter((p) => p.kind === "before").length;
      totalCompletion += completion_photos;
      totalIssue += issue_photos;
      return { ...j, completion_photos, issue_photos };
    });
    s.completion_photos = totalCompletion;
    s.issue_photos = totalIssue;
    return s;
  },
  products: () => http.get("/products").then((r) => r.data),
  addProduct: (name) => http.post("/products", { name }).then((r) => r.data),
  deleteProduct: (id) => http.delete(`/products/${id}`).then((r) => r.data),
  lengths: () => http.get("/lengths").then((r) => r.data),
  addLength: (name) => http.post("/lengths", { name }).then((r) => r.data),
  deleteLength: (id) => http.delete(`/lengths/${id}`).then((r) => r.data),
};

const BACKEND = process.env.REACT_APP_BACKEND_URL;

// Render a photo whether the API returns a data URI or raw base64.
export function photoSrc(image_base64) {
  if (!image_base64) return "";
  return image_base64.startsWith("data:") ? image_base64 : `data:image/jpeg;base64,${image_base64}`;
}

function _fileToDataURL(file) {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(r.result);
    r.onerror = reject;
    r.readAsDataURL(file);
  });
}

function _loadImage(file) {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => { URL.revokeObjectURL(url); resolve(img); };
    img.onerror = (e) => { URL.revokeObjectURL(url); reject(e); };
    img.src = url;
  });
}

// Downscale + JPEG-compress a photo in the browser BEFORE upload. Phone photos
// are often 3-12MB; sending that as base64 over the ingress is slow and can fail.
// This caps the longest edge at maxDim and re-encodes as JPEG, typically taking
// the payload to a few hundred KB. Returns a data URI string.
export async function compressImageFile(file, maxDim = 1600, quality = 0.72) {
  if (!file || !file.type || !file.type.startsWith("image/")) {
    return await _fileToDataURL(file);
  }
  try {
    let source;
    try {
      source = await createImageBitmap(file, { imageOrientation: "from-image" });
    } catch {
      source = await _loadImage(file);
    }
    const sw = source.width, sh = source.height;
    if (!sw || !sh) return await _fileToDataURL(file);
    const scale = Math.min(1, maxDim / Math.max(sw, sh));
    const w = Math.max(1, Math.round(sw * scale));
    const h = Math.max(1, Math.round(sh * scale));
    const canvas = document.createElement("canvas");
    canvas.width = w; canvas.height = h;
    const ctx = canvas.getContext("2d");
    ctx.drawImage(source, 0, 0, w, h);
    if (source.close) source.close();
    const out = canvas.toDataURL("image/jpeg", quality);
    // Guard: if compression somehow produced something larger, keep the smaller one.
    if (out && out.length > 100) return out;
    return await _fileToDataURL(file);
  } catch {
    return await _fileToDataURL(file);
  }
}

// Capture the device's GPS once (best-effort). Never rejects — resolves with
// { available:false } if unavailable or the user denies permission.
export function captureGps() {
  return new Promise((resolve) => {
    if (!("geolocation" in navigator)) return resolve({ available: false });
    navigator.geolocation.getCurrentPosition(
      (pos) => resolve({ available: true, lat: pos.coords.latitude, lng: pos.coords.longitude, accuracy: pos.coords.accuracy }),
      () => resolve({ available: false }),
      { enableHighAccuracy: true, timeout: 8000, maximumAge: 30000 }
    );
  });
}

// Open a job's PDF report. We generate it on our own backend (downscaling photos)
// because the upstream PDF endpoint times out (520) for jobs with large photos.
// We open the tab synchronously (inside the click gesture) BEFORE the await so the
// browser doesn't block it, then point it at the generated PDF blob.
export async function openJobReport(jobId) {
  return openAuthedPdf(`/reports/job-local/${jobId}.pdf`);
}

// Open a protected PDF in a new tab (tab is opened first so pop-up blockers allow it).
export async function openAuthedPdf(path) {
  const win = window.open("", "_blank");
  try {
    const resp = await http.get(path, { responseType: "blob" });
    const blobUrl = URL.createObjectURL(resp.data);
    if (win && !win.closed) win.location.href = blobUrl;
    else window.open(blobUrl, "_blank");
    setTimeout(() => URL.revokeObjectURL(blobUrl), 60000);
  } catch (e) {
    if (win && !win.closed) win.close();
    throw e;
  }
}

// Fetch a protected file with the bearer token, then download it or open it in a tab.
export async function downloadAuthed(path, filename, openInTab = false) {
  const token = tokenStore.get();
  const headers = { Authorization: `Bearer ${token}` };
  if (depotStore.get()) headers["X-Depot-Id"] = depotStore.get();
  const res = await fetch(`${BACKEND}/api${path}`, { headers });
  if (!res.ok) {
    let msg = `Download failed (${res.status})`;
    try { const j = await res.json(); msg = j.detail || msg; } catch { /* ignore */ }
    throw new Error(msg);
  }
  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  if (openInTab) {
    window.open(url, "_blank", "noopener");
    setTimeout(() => URL.revokeObjectURL(url), 60000);
  } else {
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  }
}
